import React from 'react';
import { Zap, Target, ChevronRight } from 'lucide-react';

/**
 * En-tête de section d'une liste d'attendus partitionnée par rubrique
 * (programme 2025) : « Objectifs d'apprentissage » ou « Automatismes ».
 *
 * Règle d'usage (cf. partitionnerParRubrique) : ces en-têtes ne s'affichent
 * QUE lorsque le descripteur contient au moins un automatisme — un
 * descripteur 100 % objectifs reste une liste nue, sans aucun en-tête.
 * Le composant ne décide pas, l'appelant si.
 *
 * `className` adapte marges/retraits au contexte ; `sombre` bascule la
 * teinte pour les panels à fond sombre. Avec `onToggle`, l'en-tête devient
 * un nœud d'arborescence repliable : chevron + compteur `count`, état
 * `ouvert` piloté par l'appelant.
 */
const STYLES = {
    objectif: {
        Icone: Target,
        libelle: 'Objectifs',
        clair: { icone: 'text-sky-500', texte: 'text-sky-600', badge: 'text-sky-700 bg-sky-100', ligne: 'bg-sky-200', chevron: 'text-sky-500' },
        sombre: { icone: 'text-sky-400', texte: 'text-sky-400', badge: 'text-sky-400/80 bg-sky-400/10', ligne: 'bg-sky-400/20', chevron: 'text-sky-400/70' },
    },
    automatisme: {
        Icone: Zap,
        libelle: 'Automatismes',
        clair: { icone: 'text-amber-500', texte: 'text-amber-600', badge: 'text-amber-700 bg-amber-100', ligne: 'bg-amber-200', chevron: 'text-amber-500' },
        sombre: { icone: 'text-amber-400', texte: 'text-amber-400', badge: 'text-amber-400/80 bg-amber-400/10', ligne: 'bg-amber-400/20', chevron: 'text-amber-400/70' },
    },
};

const SeparateurRubrique = ({ rubrique = 'automatisme', className = '', sombre = false, onToggle, ouvert = true, count }) => {
    const style = STYLES[rubrique] || STYLES.automatisme;
    const t = sombre ? style.sombre : style.clair;
    const { Icone } = style;
    const contenu = (
        <>
            <Icone className={`w-3 h-3 flex-shrink-0 ${t.icone}`} />
            <span className={`text-[10px] font-bold uppercase tracking-wider ${t.texte}`}>
                {style.libelle}
            </span>
            {count !== undefined ? (
                <span className={`text-[10px] px-1.5 py-[1px] rounded-md ${t.badge}`}>
                    {count}
                </span>
            ) : null}
            <div className={`flex-1 h-px ${t.ligne}`} />
            {onToggle ? (
                <ChevronRight
                    size={11}
                    className={`flex-shrink-0 transition-transform duration-200 ${t.chevron}`}
                    style={{ transform: ouvert ? 'rotate(90deg)' : 'none' }}
                />
            ) : null}
        </>
    );
    if (onToggle) {
        return (
            <button
                type="button"
                onClick={onToggle}
                className={`w-full flex items-center gap-1.5 pt-2 pb-0.5 cursor-pointer ${className}`}
            >
                {contenu}
            </button>
        );
    }
    return <div className={`flex items-center gap-1.5 pt-2 pb-0.5 ${className}`}>{contenu}</div>;
};

export default SeparateurRubrique;
